<template>
  <div class="article-content" v-html="processedContent"></div>
</template>

<script>
const SITE_HOSTS = ['gamesnest.org', 'www.gamesnest.org']

export default {
  name: 'ArticleContent',
  props: {
    content: {
      type: String,
      required: true,
    },
  },
  computed: {
    processedContent() {
      if (!this.content) return ''

      return this.content.replace(
        /<a\s+([^>]*href=["'])([^"']+)(["'][^>]*)>([\s\S]*?)<\/a>/gi,
        (match, beforeHref, href, afterHref, innerHtml) => {
          const normalizedHref = this.normalizeInternalHref(href)
          if (!normalizedHref) {
            return innerHtml
          }

          const attrs = `${beforeHref}${normalizedHref}${afterHref}`
            .replace(/\s+(?:target|rel|referrerpolicy)=(['"]).*?\1/gi, '')
          return `<a ${attrs}>${innerHtml}</a>`
        }
      )
    },
  },
  methods: {
    ensureTrailingSlash(href) {
      if (!href || href.startsWith('#') || href.startsWith('?')) return href

      let suffix = ''
      let path = href
      for (const marker of ['#', '?']) {
        const index = path.indexOf(marker)
        if (index !== -1) {
          suffix = `${path.slice(index)}${suffix}`
          path = path.slice(0, index)
        }
      }

      if (path && path !== '/' && !path.endsWith('/')) {
        const lastSegment = path.split('/').pop() || ''
        if (!lastSegment.includes('.')) {
          path = `${path}/`
        }
      }

      return `${path}${suffix}`
    },
    normalizeInternalHref(href) {
      const candidate = String(href || '').trim()
      if (!candidate) return null
      if (candidate.startsWith('#') || candidate.startsWith('?')) return candidate
      if (candidate.startsWith('/')) return this.ensureTrailingSlash(candidate)

      try {
        const absoluteLike = /^(?:https?:)?\/\//i.test(candidate)
        const url = absoluteLike
          ? new URL(candidate.startsWith('//') ? `https:${candidate}` : candidate)
          : new URL(candidate, 'https://gamesnest.org')

        if (absoluteLike) {
          if (!SITE_HOSTS.includes(url.host)) return null
          return this.ensureTrailingSlash(`${url.pathname}${url.search}${url.hash}` || '/')
        }

        if (/^[a-z][a-z0-9+.-]*:/i.test(candidate)) {
          return null
        }

        if (candidate.includes(' ')) {
          return null
        }

        return this.ensureTrailingSlash(`${url.pathname}${url.search}${url.hash}` || '/')
      } catch (error) {
        return null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.article-content {
  font-size: $font-size-base;
  line-height: 1.8;
  color: $text-primary;

  ::v-deep {
    h1, h2, h3, h4, h5, h6 {
      margin: $spacing-xl 0 $spacing-lg 0;
      font-weight: 600;
      line-height: 1.4;
      color: $text-primary;
    }

    h1 {
      font-size: $font-size-xxl;
    }

    h2 {
      font-size: $font-size-xl;
    }

    h3 {
      font-size: $font-size-lg;
    }

    p {
      margin: $spacing-md 0;
    }

    a {
      color: $link-color;
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.2s;

      &:hover {
        border-bottom-color: $link-color;
      }
    }

    ul, ol {
      margin: $spacing-md 0;
      padding-left: $spacing-xl;
    }

    li {
      margin: $spacing-sm 0;
    }

    blockquote {
      margin: $spacing-lg 0;
      padding: $spacing-md $spacing-lg;
      border-left: 4px solid $primary-color;
      background-color: $bg-secondary;
      color: $text-secondary;
      font-style: italic;
    }

    code {
      padding: 2px 6px;
      background-color: $bg-secondary;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      margin: $spacing-lg 0;
      padding: $spacing-lg;
      background-color: $bg-secondary;
      border-radius: 8px;
      overflow-x: auto;

      code {
        padding: 0;
        background-color: transparent;
      }
    }

    img {
      max-width: 100%;
      height: auto;
      margin: $spacing-lg 0;
      border-radius: 8px;
    }

    table {
      width: 100%;
      margin: $spacing-lg 0;
      border-collapse: collapse;

      th, td {
        padding: $spacing-sm $spacing-md;
        border: 1px solid $border-color;
        text-align: left;
      }

      th {
        background-color: $bg-secondary;
        font-weight: 600;
      }
    }

    hr {
      margin: $spacing-xl 0;
      border: none;
      border-top: 1px solid $border-color;
    }

    @include mobile {
      h1 {
        font-size: $font-size-xl;
      }

      h2 {
        font-size: $font-size-lg;
      }

      h3 {
        font-size: $font-size-base;
      }
    }
  }
}
</style>
